import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useBatchOverview, useTrainerBatches } from '@/lib/hooks/useTrainer';
import { computePacing, pacingClasses } from '@/lib/pacing';
import { groupSessionsByDate, mostUrgentUnmarkedSession, relativeTime } from '@/lib/trainerSessions';
import { useSessionAttendance } from '@/lib/hooks/useSessionAttendance';
import { TrainerCard } from '@/features/TrainerLayout/primitives/TrainerCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { AttendanceSheet } from '@/features/Trainer/AttendanceSheet';
import { trainerApi, type Announcement, type StudentSummary } from '@/lib/api/trainer';
import { queryKeys } from '@/lib/queryKeys';

export const Route = createFileRoute('/trainer/batches/$batchId/overview')({
  component: OverviewTab,
});

interface AttentionRow {
  student: StudentSummary;
  reasons: string[];
}

function mergeAttention(students: StudentSummary[]): AttentionRow[] {
  return students
    .map((student) => {
      const reasons: string[] = [];
      if (student.progress_pct < 20) reasons.push('Behind');
      if (student.is_flagged) reasons.push('Flagged');
      return { student, reasons };
    })
    .filter((row) => row.reasons.length > 0);
}

export function OverviewTab() {
  const { batchId } = Route.useParams();
  const navigate = useNavigate();
  const { data: overview, isLoading } = useBatchOverview(batchId);
  const { data: trainerBatches = [] } = useTrainerBatches();
  const { data: announcements = [] } = useQuery<Announcement[]>({
    queryKey: queryKeys.trainer.batchAnnouncements(batchId),
    queryFn: async () => {
      const { data } = await trainerApi.listAnnouncements(batchId);
      return data;
    },
  });
  const { attendanceSession, openAttendance, closeAttendance } = useSessionAttendance();

  const trainerBatch = trainerBatches.find((b) => b.id === batchId) ?? null;

  const goToStudents = () => navigate({ to: '/trainer/batches/$batchId/students', params: { batchId } });
  const goToSessions = () => navigate({ to: '/trainer/batches/$batchId/sessions', params: { batchId } });
  const goToAnnouncements = () => navigate({ to: '/trainer/batches/$batchId/announcements', params: { batchId } });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#ff5a1f] border-t-transparent" />
      </div>
    );
  }

  if (!overview) {
    return <div className="flex h-64 items-center justify-center text-[#6b7280]">Batch not found.</div>;
  }

  const { stats, students, upcoming_sessions } = overview;
  const attention = mergeAttention(students);
  const heroSession = mostUrgentUnmarkedSession(upcoming_sessions);
  const nextSession = [...upcoming_sessions]
    .filter((s) => !heroSession || s.id !== heroSession.id)
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())[0];
  const upcomingPreview = groupSessionsByDate(upcoming_sessions).flatMap((g) => g.sessions).slice(0, 2);
  const latestAnnouncement = announcements[0];

  const pacing = trainerBatch
    ? computePacing(trainerBatch.start_date, trainerBatch.end_date, trainerBatch.progress_pct)
    : null;
  const pacingTone = pacing ? pacingClasses[pacing.pacing] : null;

  return (
    <div className="flex-1 space-y-5 overflow-y-auto">
      {/* Hero next action */}
      <TrainerCard className="flex flex-wrap items-center gap-5 p-6">
        <div className="flex h-13 w-13 flex-shrink-0 items-center justify-center rounded-2xl bg-[#fff1ec] text-[#ff5a1f]">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        {heroSession ? (
          <>
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-bold text-[#111827]">Take attendance — {heroSession.title}</p>
              <p className="text-sm text-[#6b7280]">Session {relativeTime(heroSession.scheduled_at)} · {students.length} students · not yet marked</p>
            </div>
            <button
              type="button"
              onClick={() => openAttendance({
                id: heroSession.id,
                title: heroSession.title,
                time: new Date(heroSession.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
              })}
              className="ml-auto flex-shrink-0 rounded-[12px] bg-[#ff5a1f] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Take Attendance →
            </button>
          </>
        ) : nextSession ? (
          <div className="min-w-0 flex-1">
            <p className="text-[17px] font-bold text-[#111827]">Next up — {nextSession.title}</p>
            <p className="text-sm text-[#6b7280]">{new Date(nextSession.scheduled_at).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</p>
          </div>
        ) : (
          <p className="text-sm text-[#6b7280]">No sessions scheduled yet. Use "Schedule Session" above to add one.</p>
        )}
      </TrainerCard>

      {/* Status cards */}
      <div className="grid gap-5 md:grid-cols-3">
        <TrainerCard className="p-6">
          <p className="text-sm font-medium text-[#6b7280]">Batch pace</p>
          {pacing && pacingTone ? (
            <>
              <p className="mt-2 text-xs text-[#9ca3af]">vs. expected completion</p>
              <p className="mt-3 text-sm">
                <span className={`font-bold ${pacingTone.text}`}>{Math.round(trainerBatch!.progress_pct)}%</span> complete · expected {Math.round(pacing.todayPct)}%
              </p>
            </>
          ) : (
            <p className="mt-2 text-xs text-[#9ca3af]">No pacing data yet.</p>
          )}
        </TrainerCard>
        <TrainerCard className="p-6">
          <p className="text-sm font-medium text-[#6b7280]">Attendance</p>
          <p className="mt-2 text-[32px] font-bold leading-none text-[#111827]">{Math.round(stats.attendance_avg)}%</p>
          <p className="mt-2 text-xs text-[#9ca3af]">avg across the batch</p>
        </TrainerCard>
        <TrainerCard className="p-6">
          <p className="text-sm font-medium text-[#6b7280]">Needs attention</p>
          <p className="mt-2 text-[32px] font-bold leading-none text-[#ff5a1f]">{attention.length}</p>
          <p className="mt-2 text-xs text-[#9ca3af]">students flagged or behind</p>
        </TrainerCard>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Students needing attention */}
        <TrainerCard>
          <div className="flex items-center justify-between p-5 pb-0">
            <h3 className="text-[15px] font-bold text-[#111827]">Students needing attention</h3>
            <button type="button" onClick={goToStudents} className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#ff5a1f]">
              View all students <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="p-3">
            {attention.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-[#6b7280]">Nothing flagged — nice.</p>
            ) : (
              attention.map(({ student, reasons }) => (
                <div
                  key={student.id}
                  data-testid={`attention-${student.username}`}
                  className="flex items-center gap-3 rounded-2xl px-2 py-2.5 hover:bg-[#fafafa]"
                >
                  <OrgAvatar name={student.username} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#111827]">{student.username}</p>
                    <p className="truncate text-xs text-[#9ca3af]">{Math.round(student.progress_pct)}% complete · {Math.round(student.attendance_pct)}% attendance</p>
                  </div>
                  <div className="flex flex-shrink-0 gap-1.5">
                    {reasons.map((reason) => (
                      <span key={reason} className="rounded-full bg-[#fff1ec] px-2.5 py-1 text-xs font-medium text-[#ff5a1f]">{reason}</span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={goToStudents}
                    aria-label="Go to student"
                    className="flex-shrink-0 rounded p-1 hover:bg-[#fafafa]"
                  >
                    <ChevronRight className="h-4 w-4 text-[#9ca3af]" />
                  </button>
                </div>
              ))
            )}
          </div>
        </TrainerCard>

        {/* Upcoming sessions preview */}
        <TrainerCard>
          <div className="flex items-center justify-between p-5 pb-0">
            <h3 className="text-[15px] font-bold text-[#111827]">Upcoming sessions</h3>
            <button type="button" onClick={goToSessions} className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#ff5a1f]">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="p-3">
            {upcomingPreview.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-[#6b7280]">No sessions scheduled.</p>
            ) : (
              upcomingPreview.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-2xl px-2 py-2.5">
                  <span className="w-16 flex-shrink-0 text-xs font-bold text-[#6b7280]">
                    {new Date(s.scheduled_at).toLocaleDateString('en-US', { weekday: 'short' })} {new Date(s.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[#111827]">{s.title}</p>
                    <p className="truncate text-xs text-[#9ca3af]">{s.topic_title}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </TrainerCard>
      </div>

      {/* Latest announcement */}
      <TrainerCard>
        <div className="flex items-center justify-between p-5 pb-0">
          <h3 className="text-[15px] font-bold text-[#111827]">Latest announcement</h3>
          <button type="button" onClick={goToAnnouncements} className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#ff5a1f]">
            View all announcements <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="p-5">
          {latestAnnouncement ? (
            <div className="rounded-2xl border border-[#ececec] px-4 py-3">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-[#6b7280]">{latestAnnouncement.creator_name || 'Trainer'}</span>
                <span className="flex-shrink-0 text-[11px] text-[#9ca3af]">{relativeTime(latestAnnouncement.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm leading-snug text-[#111827]">{latestAnnouncement.content}</p>
            </div>
          ) : (
            <p className="text-sm text-[#6b7280]">No announcements yet.</p>
          )}
        </div>
      </TrainerCard>

      {attendanceSession && (
        <AttendanceSheet
          sessionId={attendanceSession.id}
          sessionTitle={attendanceSession.title}
          sessionTime={attendanceSession.time}
          students={students}
          open={!!attendanceSession}
          onClose={closeAttendance}
          batchId={batchId}
        />
      )}
    </div>
  );
}
